/**
 * Fictional sample data for VITE_MOCK=1. All numbers are made up except
 * the 25.144 active-contacts figure (per task brief). Never real people
 * — "Mikkel" is only ever the logged-in user; link/page names use
 * "[Product page A]"-style placeholders.
 */
import { previewTimeline } from '../timeline';
import { blocksToHtml, createBlock, type Block } from '../blocks';
import { MOCK_HTML_TEMPLATE } from './htmlCheck';
import type {
  Automation,
  AutomationStats,
  AutomationVersion,
  AutoLabel,
  BrandKit,
  BuiltInFlowKey,
  Campaign,
  CampaignStatsDetail,
  CampaignStatsListItem,
  ContactDetail,
  ContactPrediction,
  ContactLabel,
  ContactListItem,
  ContactStatus,
  ConsentEntry,
  ConsentText,
  FlowDefinition,
  HealthSummary,
  HistoryEntry,
  LabelCountEntry,
  LabelSettings,
  LegalBasis,
  MailingList,
  MessageCounts,
  ReviewItem,
  ReviewStats,
  Segment,
  Sender,
  SenderDomain,
  SendingRoi,
  SourceStat,
  Template,
  TriggerType,
  WarmupAdmin,
} from '../../types/api';

export const DAY = 24 * 60 * 60 * 1000;
const now = Date.now();

export const mockCampaigns: Campaign[] = [
  {
    id: 'c-pause',
    senderId: 's-1',
    templateId: null,
    name: 'September offer (paused)',
    subject: 'A September offer',
    preheader: null,
    html: '<p>Paused</p>',
    blockJson: null,
    status: 'paused',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: now - 3 * DAY,
    createdAt: now - 3 * DAY,
    sentAt: null,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    id: 'c-pause-flow',
    senderId: 's-1',
    templateId: null,
    name: 'Welcome flow, step 1 (paused)',
    subject: 'Welcome to NoPain',
    preheader: null,
    html: '<p>Paused</p>',
    blockJson: null,
    status: 'paused',
    kind: 'flow_step',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: null,
    createdAt: now - 3 * DAY,
    sentAt: null,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    id: 'c-okt',
    senderId: 's-1',
    templateId: null,
    name: 'October newsletter',
    subject: "What's happening in October?",
    preheader: null,
    html: '<p>October draft text</p>',
    blockJson: {
      blocks: [
        { type: 'text', id: 'b-okt-1', text: 'October draft text' },
        {
          type: 'footer',
          id: 'b-okt-2',
          companyAddress: 'Eksempelvej 1, 8000 Aarhus C',
          unsubscribeHref: '{{ unsubscribe_url }}',
        },
      ],
    },
    status: 'draft',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: null,
    createdAt: now - 2 * DAY,
    sentAt: null,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    // A draft saved before campaigns stored their block JSON (review fix
    // 25/9) — the editor must show its html read-only, never a blank editor.
    id: 'c-gammel',
    senderId: 's-1',
    templateId: null,
    name: 'Old draft',
    subject: 'From before blocks were saved',
    preheader: null,
    html: '<p>Real content from an old draft</p>',
    blockJson: null,
    status: 'draft',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: null,
    createdAt: now - 3 * DAY,
    sentAt: null,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    id: 'c-efteraar',
    senderId: 's-1',
    templateId: null,
    name: 'Autumn offer',
    subject: 'The autumn offer is here',
    preheader: null,
    html: '<p>Autumn offer</p>',
    blockJson: null,
    // Waiting for approval = a draft with a test email sent (the API has no
    // separate status for it).
    status: 'draft',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: now - 3 * 60 * 60 * 1000,
    createdAt: now - 5 * DAY,
    sentAt: null,
    // Example of a draft with an A/B subject-line test set up (not yet sent).
    abEnabled: true,
    abSplitPercent: 20,
    abWaitHours: 6,
    subjectB: 'The autumn offer is waiting for you',
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    id: 'c-sep',
    senderId: 's-1',
    templateId: null,
    name: 'September newsletter',
    subject: 'September newsletter',
    preheader: 'Three things worth knowing this month',
    html: '<p>September</p>',
    blockJson: null,
    status: 'sent',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: 'u-mikkel',
    approvedAt: now - 6 * DAY,
    testSendDoneAt: now - 6 * DAY,
    createdAt: now - 8 * DAY,
    sentAt: now - 6 * DAY,
    // Example of a completed A/B test: variant B won on click rate.
    abEnabled: true,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: "It's happening in September",
    // K6: B also had its own content.
    htmlB: '<p>September, version B</p>',
    abTestStartedAt: now - 6 * DAY,
    abWinnerVariant: 'b',
    abWinnerDecidedAt: now - 6 * DAY + 4 * 60 * 60 * 1000,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  {
    id: 'c-sommer',
    senderId: 's-1',
    templateId: null,
    name: 'Summer sale – last chance',
    subject: 'Last chance!',
    preheader: null,
    html: '<p>Summer sale</p>',
    blockJson: null,
    status: 'sent',
    kind: 'campaign',
    scheduledAt: null,
    createdBy: 'u-mikkel',
    approvedBy: 'u-mikkel',
    approvedAt: now - 27 * DAY,
    testSendDoneAt: now - 27 * DAY,
    createdAt: now - 30 * DAY,
    sentAt: now - 27 * DAY,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  },
  // One send in three languages ("Name (XX)"): the list shows it as one
  // row and "Send all" approves the drafts together. DA and DE have their
  // test email; EN does not yet.
  ...(['DA', 'DE', 'EN'] as const).map((lang, i): Campaign => ({
    id: `c-bf-${lang.toLowerCase()}`,
    senderId: 's-1',
    templateId: null,
    name: `Black Friday 2026 (${lang})`,
    subject: 'Black Friday',
    preheader: null,
    html: `<p>Black Friday (${lang})</p>`,
    blockJson: null,
    status: 'draft',
    kind: 'campaign',
    scheduledAt: now + 2 * DAY,
    createdBy: 'u-mikkel',
    approvedBy: null,
    approvedAt: null,
    testSendDoneAt: lang === 'EN' ? null : now - 60 * 60 * 1000,
    createdAt: now - 2 * DAY + i,
    sentAt: null,
    abEnabled: false,
    abSplitPercent: 15,
    abWaitHours: 4,
    subjectB: null,
    abTestStartedAt: null,
    abWinnerVariant: null,
    abWinnerDecidedAt: null,
    abNoSignificantWinner: false,
    resendOfCampaignId: null,
    resendKind: null,
  })),
];

/** Sample data — built with `lib/blocks.ts` so the html matches what the editor itself would render. */
function templateBlocks(text: string): Block[] {
  const textBlock = createBlock('text') as Extract<Block, { type: 'text' }>;
  const footerBlock = createBlock('footer') as Extract<Block, { type: 'footer' }>;
  return [
    { ...textBlock, text },
    { ...footerBlock, companyAddress: 'NoPain ApS, Eksempelvej 1, 8000 Aarhus' },
  ];
}

function toTemplate(id: string, name: string, text: string, updatedAt: number): Template {
  const blocks = templateBlocks(text);
  const html = blocksToHtml(blocks);
  return {
    id,
    name,
    kind: 'blocks',
    blockJson: { blocks },
    html,
    textFallback: text,
    createdAt: updatedAt - 5 * DAY,
    updatedAt,
  };
}

/** Sample data — 5 templates, 4 of them used in the installed flows/campaigns (see mockBuiltinFlows), one unused. */
export const mockTemplates: Template[] = [
  toTemplate(
    'tpl-velkomst',
    'Welcome mail',
    'Welcome to NoPain! We are glad to have you with us.',
    now - 30 * DAY,
  ),
  toTemplate(
    'tpl-cremetips',
    'How to use the cream',
    'Here are a few good tips to get the most out of your cream.',
    now - 30 * DAY,
  ),
  toTemplate(
    'tpl-rabatkode',
    'Your discount code',
    'Here is your personal discount code — use it on your next purchase.',
    now - 30 * DAY,
  ),
  toTemplate(
    'tpl-glemt-kurv',
    'You forgot something in your cart',
    'We saved your cart in case you are still interested.',
    now - 12 * DAY,
  ),
  toTemplate(
    'tpl-sidste-chance',
    'Last chance — cart waiting',
    'Your cart is still waiting. Last chance to complete the purchase.',
    now - 12 * DAY,
  ),
  {
    id: 'tpl-html-tilbud',
    name: 'Tattoo bundle offer (imported HTML)',
    kind: 'html',
    blockJson: null,
    html: MOCK_HTML_TEMPLATE,
    textFallback: 'Hi, the tattoo bundle is back.',
    createdAt: now - 6 * DAY,
    updatedAt: now - 2 * DAY,
  },
  toTemplate(
    'tpl-nyhedsbrev',
    'General newsletter template',
    'A general template for future newsletters. Not in use yet.',
    now - 40 * DAY,
  ),
];

export const mockHealthSummary: HealthSummary = {
  bounce: {
    rate: 0.004,
    warnAt: 0.01,
    pauseAt: 0.02,
    window: '7d',
    attempted: 2500,
    minSample: 20,
  },
  complaint: {
    rate: 0.0002,
    warnAt: 0.0005,
    pauseAt: 0.001,
    window: '7d',
    attempted: 2500,
    minSample: 20,
  },
  warmup: {
    day: 3,
    totalDays: 14,
    limitToday: 2000,
    sentToday: 1240,
    schedule: Array.from({ length: 14 }, (_, i) => ({ day: i, limit: 200 * (i + 1) })),
    target: 25_144,
    paused: false,
    startedAt: now - 6 * DAY,
  },
  ses: { mode: 'fake' },
  unknownMessages: 0,
  queue: { queued: 0, retryable: 1 },
  unsubscribeLog: { lastEntryAt: now - 2 * 60 * 60 * 1000, count7d: 12 },
  killSwitch: { engaged: false },
  senderPaused: {
    paused: false,
    reason: null,
    kind: null,
    errorCode: null,
    httpStatus: null,
    pausedAt: null,
    senderId: null,
  },
  stuckFlowRuns: 0,
};

export const mockStatsList: CampaignStatsListItem[] = [
  {
    id: 'c-sep',
    name: 'September newsletter',
    status: 'sent',
    sentAt: now - 6 * DAY,
    sent: 24902,
    delivered: 24781,
    clicked: 843,
    unsubscribed: 50,
    bounced: 99,
    complained: 5,
  },
  {
    id: 'c-sommer',
    name: 'Summer sale – last chance',
    status: 'sent',
    sentAt: now - 27 * DAY,
    sent: 24800,
    delivered: 24666,
    clicked: 719,
    unsubscribed: 62,
    bounced: 118,
    complained: 7,
  },
];

/**
 * The mock's sending ROI for `sent` mails at the default 0.09 per 1,000
 * (`GET /api/stats/campaigns/:id` → `roi`); the handlers re-price it when
 * the setting changes. Made-up orders and revenue.
 */
export function mockSendingRoi(
  sent: number,
  orders: number,
  revenue: number,
  costPerThousand = 0.09,
  onHold: { orders: number; revenue: number } = { orders: 0, revenue: 0 },
): SendingRoi {
  const cost = Math.round(((sent * costPerThousand) / 1000) * 10_000) / 10_000;
  return {
    sent,
    costPerThousand,
    cost,
    orders,
    revenue,
    onHoldOrders: onHold.orders,
    onHoldRevenue: onHold.revenue,
    roi: cost > 0 ? revenue / cost : null,
    currency: 'EUR',
  };
}

export const mockStatsDetail: Record<string, CampaignStatsDetail> = {
  'c-sep': {
    id: 'c-sep',
    name: 'September newsletter',
    sent: 24902,
    delivered: 24781,
    clicked: 843,
    unsubscribed: 50,
    bounced: 99,
    complained: 5,
    opensTracked: false,
    clicksPerHour: Array.from({ length: 48 }, (_, h) => ({
      hourStart: now - 6 * DAY + h * 60 * 60 * 1000,
      clicks: Math.max(0, Math.round(210 * Math.exp(-h / 8))),
    })),
    topLinks: [
      { url: '[Product page A]', clicks: 412, uniqueClicks: 388 },
      { url: '[Offer page]', clicks: 236, uniqueClicks: 221 },
      { url: '[Product page B]', clicks: 119, uniqueClicks: 110 },
      { url: '[Forside]', clicks: 76, uniqueClicks: 70 },
    ],
    ab: {
      splitPercent: 15,
      waitHours: 4,
      subjectB: "It's happening in September",
      contentDiffers: true,
      winnerVariant: 'b',
      winnerDecidedAt: now - 6 * DAY + 4 * 60 * 60 * 1000,
      noSignificantWinner: false,
      stats: {
        a: { recipients: 1870, uniqueClicks: 58 },
        b: { recipients: 1870, uniqueClicks: 89 },
      },
    },
    roi: mockSendingRoi(24902, 96, 9340, 0.09, { orders: 4, revenue: 85 }),
    orders: [
      {
        orderId: '10482',
        orderedAt: now - 5 * DAY,
        amount: 156.95,
        currency: 'EUR',
        contactId: 'contact-2',
        email: 'kontakt2@eksempel.dk',
      },
      {
        orderId: '10471',
        orderedAt: now - 6 * DAY,
        amount: 40.05,
        currency: 'EUR',
        contactId: null,
        email: null,
      },
    ],
  },
  'c-sommer': {
    id: 'c-sommer',
    name: 'Summer sale – last chance',
    sent: 24800,
    delivered: 24666,
    clicked: 719,
    unsubscribed: 62,
    bounced: 118,
    complained: 7,
    opensTracked: false,
    clicksPerHour: Array.from({ length: 48 }, (_, h) => ({
      hourStart: now - 27 * DAY + h * 60 * 60 * 1000,
      clicks: Math.max(0, Math.round(150 * Math.exp(-h / 10))),
    })),
    topLinks: [
      { url: '[Offer page]', clicks: 301, uniqueClicks: 280 },
      { url: '[Product page A]', clicks: 190, uniqueClicks: 175 },
      { url: '[Forside]', clicks: 88, uniqueClicks: 80 },
    ],
    ab: null,
    roi: mockSendingRoi(24800, 71, 6120),
    orders: [],
  },
};

const consentCategories = ['Newsletter', 'Offer', 'Product updates'];
const contactStatusCycle: ContactStatus[] = [
  'active',
  'active',
  'active',
  'suppressed',
  'suppressed',
];
// Sample language/country pairs (every third contact has none, like a contact whose source had neither).
const mockLanguageCountry: [string | null, string | null][] = [
  ['da', 'DK'],
  ['de', 'DE'],
  [null, null],
  ['en', 'GB'],
  ['da', null],
  [null, 'SE'],
];
export const mockContacts: ContactListItem[] = Array.from({ length: 42 }, (_, i) => ({
  id: `contact-${i + 1}`,
  email: `kontakt${i + 1}@eksempel.dk`,
  firstName: i % 4 === 0 ? 'Mette' : null,
  lastName: i % 4 === 0 ? 'Jensen' : null,
  language: mockLanguageCountry[i % mockLanguageCountry.length]?.[0] ?? null,
  country: mockLanguageCountry[i % mockLanguageCountry.length]?.[1] ?? null,
  status: contactStatusCycle[i % contactStatusCycle.length] as ContactStatus,
  // contact-2 is a buyer an order put on the list: no consent category.
  consentCategory: i === 1 ? null : (consentCategories[i % consentCategories.length] as string),
  orderSignupAt: i === 1 ? now - (i + 1) * DAY : null,
  createdAt: now - (i + 1) * DAY,
  lastSentAt: i % 3 === 0 ? now - 6 * DAY : null,
  lastOrderAt: i % 2 === 0 ? now - (20 + i * 15) * DAY : null,
}));

export function mockContactDetail(id: string): ContactDetail | undefined {
  const base = mockContacts.find((c) => c.id === id);
  if (!base) return undefined;
  return {
    ...base,
    source: 'Mailchimp-eksport 23/9',
    nameSource: base.firstName || base.lastName ? 'import' : null,
    languageSource: base.language ? (base.country ? 'self' : 'import') : null,
    countrySource: base.country ? (base.language ? 'self' : 'import') : null,
    city: base.country ? 'Berlin' : null,
    postalCode: base.country ? '10115' : null,
    region: null,
    timezone: base.country ? 'Europe/Berlin' : null,
    mailchimp: base.country
      ? {
          leid: '1234567',
          euid: 'a1b2c3d4e5',
          memberRating: 2,
          lastChangedAt: now - 30 * DAY,
          importedAt: now - 20 * DAY,
        }
      : null,
    consentText: 'Jeg giver samtykke til at modtage nyhedsbrevet.',
    consentAt: base.createdAt,
    ...(id === 'contact-2' ? { source: 'order' } : {}),
  };
}

/** K6: an estimate for every other mock contact; the rest have too little order history. */
export function mockContactPrediction(id: string): ContactPrediction | null {
  const n = Number(/\d+$/.exec(id)?.[0] ?? 0);
  if (n % 2 === 1) return null;
  const churnRisk = (['low', 'medium', 'high'] as const)[n % 3]!;
  return {
    basis: n % 4 === 0 ? 'contact' : 'shop',
    gapDays: 62.5,
    expectedNextOrderAt: now + (churnRisk === 'low' ? 12 : -40) * DAY,
    churnRisk,
    expectedOrders: churnRisk === 'low' ? 5.84 : churnRisk === 'medium' ? 2.92 : 0.58,
    predictedValue: 240 + n * 3,
    spent: 120 + n,
    currency: 'EUR',
  };
}

export function mockConsentHistory(_id: string): ConsentEntry[] {
  return [
    {
      at: now - 10 * DAY,
      action: 'given',
      source: 'Mailchimp-eksport 23/9',
      text: 'Jeg giver samtykke til at modtage nyhedsbrevet.',
      textVersion: 'v1',
    },
  ];
}

export const mockConsentTexts: ConsentText[] = [
  {
    version: 'v1',
    text: 'Jeg giver samtykke til at modtage nyhedsbrevet.',
    kind: 'form',
    language: null,
    createdAt: now - 30 * DAY,
    firstUsedAt: now - 10 * DAY,
    locked: true,
  },
];

export function mockContactHistory(_id: string): HistoryEntry[] {
  return [
    {
      at: now - 6 * DAY,
      type: 'sent',
      campaignId: 'c-sep',
      campaignName: 'September newsletter',
      detail: null,
    },
    {
      at: now - 6 * DAY + 60 * 60 * 1000,
      type: 'click',
      campaignId: 'c-sep',
      campaignName: 'September newsletter',
      detail: '[Product page A]',
    },
  ];
}

export const mockBrandKit: BrandKit = {
  logoUrl: null,
  primaryColor: '#5457D6',
  textColor: '#18181B',
  backgroundColor: '#FFFFFF',
  footerHtml: '<p>NoPain ApS · Eksempelvej 1, 1000 København</p>',
  companyAddress: 'NoPain® Cream',
  legalNoticeUrl: 'https://nopaincream.com/en/legal-notice/',
  defaultSenderId: 's-1',
  utm: { enabled: true, source: 'newsletter', medium: 'email' },
  trustpilotReviewUrl: 'https://dk.trustpilot.com/review/nopain.example',
  reviewCustomerServiceEmail: 'support@nopain.example',
  updatedAt: now - 10 * DAY,
};

/** Follow-up queue from the anmeldelsesflow (28/9) — low ratings and/or free-text feedback. */
export const mockReviewItems: ReviewItem[] = [
  {
    id: 'rv-1',
    orderId: 'order-1042',
    contactId: 'k-1',
    rating: 2,
    feedback: 'Min pakke kom i stykker under transporten. Kan I sende en ny?',
    status: 'open',
    createdAt: now - 2 * DAY,
    updatedAt: now - 2 * DAY,
    resolvedAt: null,
    resolvedBy: null,
  },
  {
    id: 'rv-2',
    orderId: 'order-1039',
    contactId: 'k-2',
    rating: 1,
    feedback: 'Leveringen var en uge forsinket uden besked.',
    status: 'open',
    createdAt: now - 4 * DAY,
    updatedAt: now - 4 * DAY,
    resolvedAt: null,
    resolvedBy: null,
  },
  {
    id: 'rv-3',
    orderId: 'order-1021',
    contactId: 'k-3',
    rating: 3,
    feedback: 'Produktet var fint, men emballagen var beskadiget.',
    status: 'resolved',
    createdAt: now - 12 * DAY,
    updatedAt: now - 9 * DAY,
    resolvedAt: now - 9 * DAY,
    resolvedBy: 'mikkel@nopain.example',
  },
];

export const mockReviewStats: ReviewStats = {
  total: 58,
  byRating: { '1': 3, '2': 4, '3': 6, '4': 15, '5': 30 },
};

export const mockCounts: MessageCounts = {
  queued: 759,
  sending: 0,
  sent: 1240,
  retryable: 1,
  failed: 0,
  unknown: 0,
  unsubscribed: 3,
  // 22,858 of the sample campaign's 24,861 messages are already delivered.
  delivered: 22858,
  total: 24861,
};

// --- Customer tags (phase 5, "sample data") ---

/**
 * Sample data for `GET /api/labels`'s `counts` — an ARRAY, matching the
 * real backend shape (see types/api.ts#LabelsOverviewResponse), not a
 * `Record<AutoLabel, number>`. The 8 automatic tags (docs/api-ui.md
 * "Kundemærkater") plus two manual tags (P7 Mailchimp-import tags), so the
 * audience picker's "all labels, not just the hardcoded auto ones" has
 * something manual to show in mock mode too.
 */
export const mockLabelCounts: LabelCountEntry[] = [
  { label: 'new_customer', count: 12 },
  { label: 'repeat', count: 40 },
  { label: 'loyal', count: 8 },
  { label: 'platinum', count: 6 },
  { label: 'vip', count: 3 },
  { label: 'inactive', count: 21 },
  { label: 'lost', count: 5 },
  { label: 'subscriber_only', count: 900 },
  { label: 'dk', count: 640 },
  { label: 'b2b', count: 58 },
];

export const mockLabelSettings: LabelSettings = {
  loyalOrderCount: 3,
  platinumThresholdAmount: 500,
  vipThresholdAmount: 2000,
  inactiveAfterDays: 90,
  lostAfterDays: 365,
  softOptinMaxDays: 730,
  singleOptinCountsAsOptin: false,
  timeOnlyCountsAsOptin: false,
  currency: 'EUR',
  updatedAt: now - 10 * DAY,
};

/** Sample data — one manual tag, set by a user (free text). */
export const mockManualLabel = 'Ambassador';

const autoLabelCycle: AutoLabel[] = [
  'subscriber_only',
  'new_customer',
  'repeat',
  'loyal',
  'platinum',
  'vip',
  'inactive',
  'lost',
];

/** Sample data — each sample contact gets one auto tag; contact 1 also has the manual tag. */
export function mockContactLabels(id: string): ContactLabel[] {
  const idx = mockContacts.findIndex((c) => c.id === id);
  const auto = autoLabelCycle[(idx < 0 ? 0 : idx) % autoLabelCycle.length] as AutoLabel;
  const labels: ContactLabel[] = [{ label: auto, source: 'auto' }];
  if (id === 'contact-1') labels.push({ label: mockManualLabel, source: 'manual' });
  return labels;
}

// --- Automatiseringer (fase 5, "Eksempeldata") ---
// Trigger-typer, slugs, navne og definitioner er kopieret fra de rigtige
// built-ins (packages/core/src/flows/builtins/*.ts) så mock-laget matcher
// den ægte API 1:1 — kun id'er, tal og tidsstempler er opdigtede.

interface BuiltinCatalogItem {
  key: BuiltInFlowKey;
  slug: string;
  name: string;
  triggerType: TriggerType;
  allowedLegalBasis: LegalBasis[];
  allowReentry: boolean;
  stopOnPurchase: boolean;
  definition: FlowDefinition;
}

const D = (n: number) => n * 24 * 60 * 60;

export const mockBuiltinFlows: BuiltinCatalogItem[] = [
  {
    key: 'welcome',
    slug: 'welcome',
    name: 'Velkomst',
    triggerType: 'contact.confirmed',
    allowedLegalBasis: ['optin'],
    allowReentry: false,
    stopOnPurchase: true,
    definition: {
      steps: [
        {
          id: 's1',
          type: 'send_template',
          templateId: 'tpl-velkomst',
          subject: 'Velkommen til NoPain',
          purpose: 'marketing',
          variants: [
            { language: 'de', templateId: 'tpl-velkomst', subject: 'Willkommen bei NoPain' },
          ],
        },
        { id: 's2', type: 'wait', seconds: D(2) },
        {
          id: 's3',
          type: 'send_template',
          templateId: 'tpl-cremetips',
          subject: 'How to get the most out of your cream',
          purpose: 'marketing',
        },
        { id: 's4', type: 'wait', seconds: D(6) },
        { id: 's5', type: 'create_coupon', percent: 10, validDays: 14, prefix: 'WELCOME' },
        {
          id: 's6',
          type: 'send_template',
          templateId: 'tpl-rabatkode',
          subject: 'Her er din rabatkode',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'post_first_purchase',
    slug: 'post-first-purchase',
    name: 'Post first purchase',
    triggerType: 'order.first',
    allowedLegalBasis: ['optin', 'soft_optin', 'customer'],
    allowReentry: false,
    stopOnPurchase: false,
    definition: {
      steps: [
        { id: 's1', type: 'wait', seconds: D(1) },
        {
          id: 's2',
          type: 'send_template',
          templateId: null,
          subject: 'Thanks for your purchase — how to use it',
          purpose: 'service',
        },
        { id: 's3', type: 'wait', seconds: D(5) },
        {
          id: 's4',
          type: 'send_template',
          templateId: null,
          subject: 'Other customers love this too',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'replenishment',
    slug: 'replenishment',
    name: 'Genbestilling',
    triggerType: 'order.completed',
    allowedLegalBasis: ['optin'],
    allowReentry: true,
    stopOnPurchase: false,
    definition: {
      steps: [
        { id: 's1', type: 'wait', seconds: D(45) },
        { id: 's2', type: 'condition', check: 'no_order_since_enrollment', onFail: 'stop' },
        {
          id: 's3',
          type: 'send_template',
          templateId: null,
          subject: 'Tid til at bestille igen?',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'abandoned_cart',
    slug: 'abandoned-cart',
    name: 'Forladt kurv',
    triggerType: 'cart.abandoned',
    allowedLegalBasis: ['optin'],
    allowReentry: true,
    stopOnPurchase: true,
    definition: {
      steps: [
        { id: 's1', type: 'wait', seconds: 60 * 60 },
        { id: 's2', type: 'condition', check: 'cart_not_recovered', onFail: 'stop' },
        {
          id: 's3',
          type: 'send_template',
          templateId: 'tpl-glemt-kurv',
          subject: 'Du glemte noget i kurven',
          purpose: 'marketing',
        },
        { id: 's4', type: 'wait', seconds: 23 * 60 * 60 },
        { id: 's5', type: 'condition', check: 'cart_not_recovered', onFail: 'stop' },
        {
          id: 's6',
          type: 'send_template',
          templateId: 'tpl-sidste-chance',
          subject: 'Sidste chance — din kurv venter stadig',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'winback',
    slug: 'winback',
    name: 'Win-back',
    triggerType: 'inactivity',
    allowedLegalBasis: ['optin'],
    allowReentry: true,
    stopOnPurchase: true,
    definition: {
      steps: [
        {
          id: 's1',
          type: 'send_template',
          templateId: null,
          subject: 'Vi har savnet dig',
          purpose: 'marketing',
        },
        { id: 's2', type: 'wait', seconds: D(7) },
        { id: 's3', type: 'condition', check: 'no_order_since_enrollment', onFail: 'stop' },
        { id: 's4', type: 'create_coupon', percent: 15, validDays: 14, prefix: 'MISSYOU' },
        {
          id: 's5',
          type: 'send_template',
          templateId: null,
          subject: 'Her er 15 % — kom tilbage',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'review',
    slug: 'review',
    name: 'Anmeldelse',
    triggerType: 'order.completed',
    allowedLegalBasis: ['optin', 'soft_optin', 'customer'],
    allowReentry: true,
    stopOnPurchase: false,
    definition: {
      steps: [
        { id: 's1', type: 'wait', seconds: D(10) },
        {
          id: 's2',
          type: 'send_template',
          templateId: null,
          subject: 'What did you think of your purchase?',
          purpose: 'review',
        },
      ],
    },
  },
  {
    key: 'birthday',
    slug: 'birthday',
    name: 'Birthday',
    triggerType: 'date.birthday',
    allowedLegalBasis: ['optin'],
    allowReentry: true,
    stopOnPurchase: false,
    definition: {
      steps: [
        { id: 's1', type: 'create_coupon', percent: 15, validDays: 14, prefix: 'BIRTHDAY' },
        {
          id: 's2',
          type: 'send_template',
          templateId: null,
          subject: 'Happy birthday 🎉',
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'anniversary',
    slug: 'anniversary',
    name: 'First-purchase anniversary',
    triggerType: 'date.first_order_anniversary',
    allowedLegalBasis: ['optin'],
    allowReentry: true,
    stopOnPurchase: false,
    definition: {
      steps: [
        {
          id: 's1',
          type: 'send_template',
          templateId: null,
          subject: "It's been a year since you became a customer",
          purpose: 'marketing',
        },
      ],
    },
  },
  {
    key: 'sunset',
    slug: 'sunset',
    name: 'Sunset',
    triggerType: 'inactivity',
    allowedLegalBasis: ['optin'],
    allowReentry: false,
    stopOnPurchase: false,
    definition: {
      steps: [
        {
          id: 's1',
          type: 'send_template',
          templateId: null,
          subject: 'Do you still want to hear from us?',
          purpose: 'marketing',
        },
        { id: 's2', type: 'wait', seconds: D(14) },
        { id: 's3', type: 'condition', check: 'no_engagement_days', days: 14, onFail: 'stop' },
        { id: 's4', type: 'update_contact', action: 'suppress_inactive' },
      ],
    },
  },
];

export function builtinTriggerConfig(key: BuiltInFlowKey): Record<string, unknown> {
  if (key === 'abandoned_cart') return { requiresCookieConsent: true };
  if (key === 'winback') return { days: 90 };
  if (key === 'sunset') return { days: 180, requiresNoEngagement: true };
  return {};
}

/** Eksempeldata — 3 af de 9 built-ins er "installeret" i eksempeldataen (resten kan installeres via knappen). */
export const mockAutomations: Automation[] = [
  { key: 'welcome', enabled: true, enabledBy: 'u-mikkel', version: 1 },
  { key: 'abandoned_cart', enabled: true, enabledBy: 'u-mikkel', version: 2 },
  { key: 'winback', enabled: false, enabledBy: null, version: 1 },
].map(({ key, enabled, enabledBy, version }, i) => {
  const builtin = mockBuiltinFlows.find((b) => b.key === key) as BuiltinCatalogItem;
  return {
    id: `auto-${builtin.slug}`,
    slug: builtin.slug,
    name: builtin.name,
    builtInKey: builtin.key,
    triggerType: builtin.triggerType,
    triggerConfig: builtinTriggerConfig(builtin.key),
    allowedLegalBasis: builtin.allowedLegalBasis,
    allowReentry: builtin.allowReentry,
    stopOnPurchase: builtin.stopOnPurchase,
    currentVersion: version,
    enabled,
    enabledBy,
    createdAt: now - (30 - i * 5) * DAY,
    updatedAt: now - (10 - i * 2) * DAY,
  } satisfies Automation;
});

export const mockAutomationStats: Record<string, AutomationStats> = {
  'auto-welcome': {
    enrolled: 214,
    active: 20,
    completed: 180,
    stopped: 14,
    sent: 402,
    expired: 0,
    deferredByFrequencyCap: 0,
    skippedByFrequencyCap: 0,
    revenue: 18650,
    currency: 'EUR',
  },
  'auto-abandoned-cart': {
    enrolled: 96,
    active: 10,
    completed: 40,
    stopped: 46,
    sent: 140,
    expired: 3,
    deferredByFrequencyCap: 5,
    skippedByFrequencyCap: 1,
    revenue: 9200,
    currency: 'EUR',
  },
  'auto-winback': {
    enrolled: 0,
    active: 0,
    completed: 0,
    stopped: 0,
    sent: 0,
    expired: 0,
    deferredByFrequencyCap: 0,
    skippedByFrequencyCap: 0,
    revenue: 0,
    currency: 'EUR',
  },
};

export const mockAutomationVersions: Record<string, AutomationVersion[]> = {
  'auto-welcome': [
    {
      version: 1,
      definition: (mockBuiltinFlows.find((b) => b.key === 'welcome') as BuiltinCatalogItem)
        .definition,
      createdBy: 'u-mikkel',
      createdAt: now - 30 * DAY,
    },
  ],
  'auto-abandoned-cart': [
    {
      version: 1,
      definition: {
        steps: (
          mockBuiltinFlows.find((b) => b.key === 'abandoned_cart') as BuiltinCatalogItem
        ).definition.steps.slice(0, 3),
      },
      createdBy: 'u-mikkel',
      createdAt: now - 25 * DAY,
    },
    {
      version: 2,
      definition: (mockBuiltinFlows.find((b) => b.key === 'abandoned_cart') as BuiltinCatalogItem)
        .definition,
      createdBy: 'u-mikkel',
      createdAt: now - 12 * DAY,
    },
  ],
  'auto-winback': [
    {
      version: 1,
      definition: (mockBuiltinFlows.find((b) => b.key === 'winback') as BuiltinCatalogItem)
        .definition,
      createdBy: 'u-mikkel',
      createdAt: now - 20 * DAY,
    },
  ],
};

export const buildMockTimeline = previewTimeline;

// --- Lists, senders and audience ---

export const mockSenderDomains: SenderDomain[] = [
  {
    id: 'd-1',
    domain: 'nopaincream.com',
    status: 'verified',
    verificationMethod: 'manual',
    verifiedAt: now - 30 * DAY,
    senderCount: 1,
    createdAt: now - 30 * DAY,
  },
];

export const mockSenders: Sender[] = [
  {
    id: 's-1',
    fromName: 'NoPain',
    fromEmail: 'sales@nopaincream.com',
    replyToEmail: 'sales@nopaincream.com',
    domainStatus: 'verified',
    dkimVerified: false,
    openTrackingEnabled: true,
    archived: false,
    archivedAt: null,
    campaignCount: 5,
    createdAt: now - 30 * DAY,
    updatedAt: null,
  },
];

/**
 * Fase-4 warm-up admin state, keyed by sender id. Mirrors
 * `generateWarmupCurve({ target: 25_144 })` from
 * packages/core/src/send/warmupCurve.ts (200 -> x1.5 every 2 days) so the
 * mock UI shows a realistic plan-vs-actual curve. `s-1` is 6 days into a
 * started, healthy warm-up (day offset 6 reached, not paused).
 */
export const mockWarmupAdmin: Record<string, WarmupAdmin> = {
  's-1': {
    dailyLimit: 50_000,
    active: true,
    paused: false,
    target: 25_144,
    startedAt: now - 6 * DAY,
    dayOffset: 6,
    limitToday: 675,
    plan: [
      { dayOffset: 0, maxSends: 200 },
      { dayOffset: 2, maxSends: 300 },
      { dayOffset: 4, maxSends: 450 },
      { dayOffset: 6, maxSends: 675 },
      { dayOffset: 8, maxSends: 1013 },
      { dayOffset: 10, maxSends: 1519 },
      { dayOffset: 12, maxSends: 2279 },
      { dayOffset: 14, maxSends: 3418 },
      { dayOffset: 16, maxSends: 5127 },
      { dayOffset: 18, maxSends: 7691 },
      { dayOffset: 20, maxSends: 11_536 },
      { dayOffset: 22, maxSends: 17_304 },
      { dayOffset: 24, maxSends: 25_144 },
    ],
  },
};

export const mockLists: MailingList[] = [
  {
    id: 'l-nyhedsbrev',
    name: 'Newsletter',
    description: 'Everyone who has signed up for the newsletter.',
    doubleOptin: true,
    archived: false,
    archivedAt: null,
    isPublic: true,
    isDefaultSignup: true,
    confirmedCount: 1200,
    campaignCount: 4,
    createdAt: now - 60 * DAY,
    updatedAt: null,
  },
  {
    id: 'l-kunder',
    name: 'Customers',
    description: null,
    doubleOptin: false,
    archived: false,
    archivedAt: null,
    isPublic: false,
    isDefaultSignup: false,
    confirmedCount: 340,
    campaignCount: 0,
    createdAt: now - 20 * DAY,
    updatedAt: null,
  },
];

export const mockSegments: Segment[] = [
  {
    id: 'seg-aarhus',
    name: 'Lives in Aarhus',
    filter: {
      version: 2,
      groups: [
        {
          labels: [],
          excludeLabels: [],
          fields: [{ key: 'CITY', op: 'eq', value: 'Aarhus' }],
          unsubscribed: false,
          sources: [],
          languages: [],
          countries: [],
          excludeCountries: [],
        },
      ],
    },
    archived: false,
    archivedAt: null,
    campaignCount: 0,
    createdAt: now - 40 * DAY,
    updatedAt: null,
  },
  {
    id: 'seg-dk',
    name: 'DK',
    filter: {
      version: 2,
      groups: [
        {
          labels: ['dk'],
          excludeLabels: [],
          fields: [],
          unsubscribed: false,
          sources: [],
          languages: [],
          countries: [],
          excludeCountries: [],
        },
      ],
    },
    archived: false,
    archivedAt: null,
    campaignCount: 2,
    createdAt: now - 20 * DAY,
    updatedAt: null,
  },
  {
    id: 'seg-alle-undtagen-tyskland',
    name: 'All except Germany',
    filter: {
      version: 2,
      groups: [
        {
          labels: [],
          excludeLabels: ['de'],
          fields: [],
          unsubscribed: false,
          sources: [],
          languages: [],
          countries: [],
          excludeCountries: [],
        },
      ],
    },
    archived: false,
    archivedAt: null,
    campaignCount: 0,
    createdAt: now - 20 * DAY,
    updatedAt: null,
  },
  {
    id: 'seg-afmeldte',
    name: 'Unsubscribed',
    filter: {
      version: 2,
      groups: [
        {
          labels: [],
          excludeLabels: [],
          fields: [],
          unsubscribed: true,
          sources: [],
          languages: [],
          countries: [],
          excludeCountries: [],
        },
      ],
    },
    archived: false,
    archivedAt: null,
    campaignCount: 0,
    createdAt: now - 10 * DAY,
    updatedAt: null,
  },
];

export const mockSourceStats: SourceStat[] = [
  { source: 'checkout', newContacts: 214, unsubscribed: 6, revenue: 18650 },
  { source: 'signup_form', newContacts: 132, unsubscribed: 11, revenue: 0 },
  { source: 'Mailchimp for WooCommerce', newContacts: 0, unsubscribed: 4, revenue: 2100 },
  { source: '(unknown)', newContacts: 3, unsubscribed: 0, revenue: 0 },
];
